"use client";

import { getAppCheckHealth, whenAppCheckReady } from "../firebase/appCheck";
import { IItem, type Item, type ItemPricePatch } from "@order-up/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { itemService } from "../services/items";
import type { MenuQuerySeed } from "./useMenuGroups";
import { createClientLogger } from "../utils/logger";

const logger = createClientLogger("useItems");

/**
 * Defines the query keys used for item-related data.
 */
const itemKeys = {
  all: ["items"] as const,
  lists: () => [...itemKeys.all, "list"] as const,
  listByRestaurant: (tenantId: number, locationNumericId: number) =>
    [...itemKeys.lists(), { tenantId, restaurantId: locationNumericId }] as const,
  details: () => [...itemKeys.all, "detail"] as const,
  detailById: (tenantId: number, locationNumericId: number, itemId: number) =>
    [...itemKeys.details(), { tenantId, restaurantId: locationNumericId, itemId }] as const,
  counts: () => [...itemKeys.all, "count"] as const,
  countByRestaurant: (tenantId: number, locationNumericId: number) =>
    [...itemKeys.counts(), { tenantId, restaurantId: locationNumericId }] as const,
};

/**
 * A hook to fetch all items belonging to a specific restaurant.
 * @param tenantId The numeric ID of the tenant.
 * @param locationNumericId The numeric ID of the restaurant.
 * @param options Optional configuration options.
 */
const useGetItemsByRestaurantId = (
  tenantId: number,
  locationNumericId: number,
  options?: {
    /** Enable real-time updates by polling Firestore at specified interval (in ms). Default: no polling */
    refetchInterval?: number;
  } & MenuQuerySeed<Item[]>,
) => {
  return useQuery({
    queryKey: itemKeys.listByRestaurant(tenantId, locationNumericId),
    queryFn: () => itemService.getItemsByRestaurantId(tenantId, locationNumericId),
    enabled: !!tenantId && !!locationNumericId,
    // Explicit cache window (matches useMenuGroups) so menu items don't depend on
    // the global QueryClient default being present — and don't refetch on every
    // mount, which slowed the menu/modal under a provider lacking that default.
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: false,
    initialData: options?.initialData,
    initialDataUpdatedAt: options?.initialDataUpdatedAt,
  });
};

/**
 * A hook to fetch the COUNT of a restaurant's items (server aggregate — no docs
 * downloaded). For count tiles/badges that only need the number.
 */
const useGetItemCount = (tenantId: number, locationNumericId: number) => {
  return useQuery({
    queryKey: itemKeys.countByRestaurant(tenantId, locationNumericId),
    queryFn: () => itemService.getItemCount(tenantId, locationNumericId),
    enabled: !!tenantId && !!locationNumericId,
  });
};

/**
 * A hook to fetch a single item by its numeric item ID.
 * @param tenantId The numeric ID of the tenant.
 * @param locationNumericId The numeric ID of the restaurant.
 * @param itemId The numeric ID of the item.
 */
const useGetItemById = (
  tenantId: number,
  locationNumericId: number,
  itemId: number,
) => {
  return useQuery({
    queryKey: itemKeys.detailById(tenantId, locationNumericId, itemId),
    queryFn: () => itemService.getItemById(tenantId, locationNumericId, itemId),
    enabled: !!tenantId && !!locationNumericId && !!itemId,
  });
};

/**
 * A hook to create a new item.
 */
const useCreateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemData,
    }: {
      tenantId: number;
      restaurantId: number;
      itemData: IItem;
    }) => itemService.createItem(tenantId, locationNumericId, itemData),
    onSuccess: (_, { tenantId, restaurantId }) => {
      // Refetch THIS restaurant's item list and its count.
      //
      // This used to also invalidate `itemKeys.all`, which is the prefix of
      // every items query in the cache — every list, every detail and every
      // count, for every restaurant the session has touched. Creating one item
      // therefore refetched all of them (and made the specific invalidation
      // above dead code, since `all` already matched it). The intent was to
      // refresh the count alongside the list, so that is what it now does.
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.countByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * A hook to update an existing item.
 */
const useUpdateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemId,
      itemData,
    }: {
      tenantId: number;
      restaurantId: number;
      itemId: number;
      itemData: Partial<IItem>;
    }) => itemService.updateItem(tenantId, locationNumericId, itemId, itemData),
    onSuccess: (_, { tenantId, restaurantId, itemId }) => {
      // Invalidate specific item
      queryClient.invalidateQueries({
        queryKey: itemKeys.detailById(tenantId, restaurantId, itemId),
      });
      // Invalidate item list for the restaurant
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Save a batch of price-sheet edits. Resolves with per-row outcomes — a
 * refused row is data, not an exception — and refreshes the item list.
 */
const useUpdateItemsPricing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      patches,
    }: {
      tenantId: number;
      restaurantId: number;
      patches: readonly ItemPricePatch[];
    }) => itemService.updateItemsPricing(tenantId, locationNumericId, patches),
    onSuccess: (result, { tenantId, restaurantId }) => {
      queryClient.invalidateQueries({ queryKey: itemKeys.listByRestaurant(tenantId, restaurantId) });
      for (const itemId of result.savedItemIds) {
        queryClient.invalidateQueries({ queryKey: itemKeys.detailById(tenantId, restaurantId, itemId) });
      }
    },
  });
};

/**
 * A hook to delete an item.
 */
const useDeleteItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemId,
    }: {
      tenantId: number;
      restaurantId: number;
      itemId: number;
    }) => itemService.deleteItem(tenantId, locationNumericId, itemId),
    onSuccess: (_, { tenantId, restaurantId, itemId }) => {
      // Invalidate specific item
      queryClient.invalidateQueries({
        queryKey: itemKeys.detailById(tenantId, restaurantId, itemId),
      });
      // Invalidate item list for the restaurant
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * A hook to create multiple items in a batch operation.
 */
const useCreateItemsBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemsData,
    }: {
      tenantId: number;
      restaurantId: number;
      itemsData: IItem[];
    }) => itemService.createItemsBatch(tenantId, locationNumericId, itemsData),
    onSuccess: (_, { tenantId, restaurantId }) => {
      // Scoped to THIS restaurant — see the note on `useCreateItem`. A bulk
      // import is exactly when a whole-namespace invalidation hurts most.
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.countByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Ensure this restaurant has its Open Food / Open Drink system items.
 *
 * Runs ONCE per (tenant, restaurant) per mount, from the management items
 * page. This is a self-healing provisioning step rather than a migration:
 * every existing restaurant grows them the first time someone opens the menu,
 * and a restaurant created tomorrow gets them through this same code — so
 * there is no separate backfill script that can be forgotten, and no second
 * provisioning path to keep in step with this one.
 *
 * A FAILURE IS DELIBERATELY SILENT. The commonest cause is a concurrent tab
 * that created them a moment earlier, which is a success by any measure that
 * matters. A genuine failure leaves the page working exactly as it does
 * today, and the next mount retries.
 */
const useEnsureSystemItems = (tenantId: number, locationNumericId: number) => {
  const queryClient = useQueryClient();
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (!tenantId || !locationNumericId) return;
    const key = `${tenantId}:${locationNumericId}`;
    if (attempted.current === key) return;
    attempted.current = key;

    let cancelled = false;
    void itemService
      .ensureSystemItems(tenantId, locationNumericId)
      .then((created) => {
        if (cancelled || created.length === 0) return;
        logger.info("Seeded system items", {
          restaurantId: locationNumericId,
          count: created.length,
        });
        queryClient.invalidateQueries({
          queryKey: itemKeys.listByRestaurant(tenantId, locationNumericId),
        });
        queryClient.invalidateQueries({
          queryKey: itemKeys.countByRestaurant(tenantId, locationNumericId),
        });
      })
      .catch((error) => {
        logger.warn("Could not ensure system items", {
          restaurantId: locationNumericId,
          error: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, locationNumericId, queryClient]);
};

/**
 * A hook to archive an item (soft delete).
 */
const useArchiveItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemId,
    }: {
      tenantId: number;
      restaurantId: number;
      itemId: number;
    }) => itemService.archiveItem(tenantId, locationNumericId, itemId),
    onSuccess: (_, { tenantId, restaurantId, itemId }) => {
      // Scoped to this item and this restaurant's list + count.
      //
      // The blanket `itemKeys.all` that used to lead this block is the prefix
      // of every items query in the cache — every list, detail and count, for
      // every restaurant — so it both refetched unrelated restaurants and made
      // the two specific invalidations below redundant.
      queryClient.invalidateQueries({
        queryKey: itemKeys.detailById(tenantId, restaurantId, itemId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.countByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * A hook to restore an archived item.
 */
const useRestoreItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      itemId,
    }: {
      tenantId: number;
      restaurantId: number;
      itemId: number;
    }) => itemService.restoreItem(tenantId, locationNumericId, itemId),
    onSuccess: (_, { tenantId, restaurantId, itemId }) => {
      // Scoped to this item and this restaurant's list + count.
      //
      // The blanket `itemKeys.all` that used to lead this block is the prefix
      // of every items query in the cache — every list, detail and count, for
      // every restaurant — so it both refetched unrelated restaurants and made
      // the two specific invalidations below redundant.
      queryClient.invalidateQueries({
        queryKey: itemKeys.detailById(tenantId, restaurantId, itemId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.countByRestaurant(tenantId, restaurantId),
      });
    },
  });
};

/**
 * A subscription hook to listen for real-time item changes in Firestore.
 * This keeps the management app in sync with POS quantity updates without requiring page refresh.
 *
 * ARCHITECTURE COMPLIANCE:
 * - ✅ Uses service layer (itemService.subscribeToItems)
 * - ✅ Service handles FirestoreRepository internally
 * - ✅ Models converted via Item.fromJSON (proper timestamp handling)
 * - ✅ No direct Firestore SDK or Repository calls in hook
 * - ✅ Proper cleanup on unmount
 * - ✅ React Query cache integration
 *
 * PATTERN: Hook → Service → Repository → Firestore
 *
 * @param tenantId The numeric ID of the tenant.
 * @param locationNumericId The numeric ID of the restaurant.
 *
 * Usage: Call this hook once at the root of your items page/component.
 * Example: useItemsSubscription(tenantId, restaurantId);
 */
const useItemsSubscription = (tenantId: number, locationNumericId: number) => {
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!tenantId || !locationNumericId) return;

    logger.debug("Setting up real-time items subscription", {
      tenantId,
      restaurantId: locationNumericId,
    });

    // `cancelled` closes the gap between unmount and an async setup that has
    // not returned yet: without it the cleanup ran first, found no handle, and
    // the listener it later created was never removed.
    let cancelled = false;

    const setupSubscription = async () => {
      // A Firestore listener's error is FINAL: one started before App Check
      // has a token can be refused once and never recover. Wait for the same
      // readiness gate every read uses; a browser App Check REFUSED gets no
      // listener at all (the page keeps the availability it opened with, and
      // checkout re-checks before any charge).
      await whenAppCheckReady();
      if (cancelled || getAppCheckHealth() === "refused") return;

      // INDUSTRY PATTERN: Hook calls service, service handles repository
      const unsubscribe = await itemService.subscribeToItems(
        tenantId,
        locationNumericId,
        (items) => {
          // Callback receives fully-converted Item models with proper Date objects
          logger.debug("Items subscription update from service", {
            count: items.length,
            sampleCreatedAt: items[0]?.createdAt instanceof Date ? "Date" : typeof items[0]?.createdAt,
          });

          // Update React Query cache with the new data
          const queryKey = itemKeys.listByRestaurant(tenantId, locationNumericId);
          queryClient.setQueryData(queryKey, items);
        },
        (error) => {
          // Error handler
          logger.error("Items subscription error", error);
        }
      );

      if (cancelled) {
        unsubscribe?.();
        return;
      }
      if (unsubscribe) {
        unsubscribeRef.current = unsubscribe;
        logger.debug("Items subscription established via service");
      }
    };

    void setupSubscription();

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        logger.debug("Cleaning up items subscription", {
          tenantId,
          restaurantId: locationNumericId,
        });
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [tenantId, locationNumericId, queryClient]);
};

/**
 * A hook to batch-update item documents to add or remove modifier group assignments.
 * Uses atomic batch writes — all item updates succeed or fail together.
 */
const useUpdateItemModifierGroupAssignments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      modifierGroupId,
      itemIdsToAdd,
      itemIdsToRemove,
      defaultRef,
    }: {
      tenantId: number;
      restaurantId: number;
      modifierGroupId: number;
      itemIdsToAdd: number[];
      itemIdsToRemove: number[];
      defaultRef: {
        isRequired: boolean;
        allowMultipleSelection: boolean;
        allowDuplicateSelection: boolean;
      };
    }) =>
      itemService.updateItemModifierGroupAssignments(
        tenantId,
        locationNumericId,
        modifierGroupId,
        itemIdsToAdd,
        itemIdsToRemove,
        defaultRef,
      ),
    onSuccess: (_, { tenantId, restaurantId }) => {
      // Attaching a modifier group changes which items carry it, so this
      // restaurant's list and every cached item DETAIL under it can be stale.
      // Scoped to the restaurant rather than the whole `itemKeys.all` namespace
      // (which spans every restaurant in the cache).
      queryClient.invalidateQueries({
        queryKey: itemKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: itemKeys.details(),
        predicate: (query) => {
          const scope = query.queryKey[query.queryKey.length - 1] as
            | { tenantId?: number; restaurantId?: number }
            | undefined;
          return scope?.tenantId === tenantId && scope?.restaurantId === restaurantId;
        },
      });
    },
  });
};

export {
  itemKeys,
  useArchiveItem,
  useEnsureSystemItems,
  useCreateItem,
  useCreateItemsBatch,
  useDeleteItem,
  useGetItemById,
  useGetItemCount,
  useGetItemsByRestaurantId,
  useItemsSubscription,
  useRestoreItem,
  useUpdateItem,
  useUpdateItemModifierGroupAssignments,
  useUpdateItemsPricing,
};
